import React, { useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import { GENERATE_INVOICES } from 'api/graphql/queries/order/generateInvoiceLink';
import { Spinner } from 'react-bootstrap';
import Button from 'components/base/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileInvoiceDollar } from '@fortawesome/free-solid-svg-icons';
import { Order as OrderType } from 'data/e-commerce/orders';
import { showErrorToast } from './ToastManager';
import { useTranslation } from 'react-i18next';

interface InvoiceButtonProps {
  selectedRows: OrderType[];
  onError: (message: string) => void;
}

const InvoiceButton: React.FC<InvoiceButtonProps> = ({ 
  selectedRows, 
  onError 
}) => {
  const { t } = useTranslation();
  const [isGenerating, setIsGenerating] = useState(false);
  
  // Check if any selected row has a status that doesn't allow invoice generation
  const hasDisabledStatusOrders = selectedRows.some(row => {
    const status = row.fulfilment_status?.label?.toLowerCase();
    return status === 'pending' || status === 'processing' || status === 'being prepared';
  });

  // Query for generating invoice download link
  const [generateInvoiceQuery, { loading: downloadLoading }] = useLazyQuery(
    GENERATE_INVOICES,
    {
      fetchPolicy: "no-cache",
      onCompleted: (data) => {
        handleDownloadResponse(data);
      },
      onError: (error) => {
        setIsGenerating(false);
        const errorMessage = error.message || "An error occurred while generating the invoice.";
        showErrorToast(errorMessage);
        onError(errorMessage);
      }
    }
  );

  // Function to handle download response from GraphQL
  const handleDownloadResponse = (data: any) => {
    setIsGenerating(false);
    const response = data?.generateInvoices;
    
    if (!response || !response.success) {
      const errorMessage = response?.message || "Failed to generate invoice. Please try again.";
      showErrorToast(errorMessage);
      onError(errorMessage);
      return;
    }
    
    try {
      const downloadUrl = response.download_url;
      
      if (!downloadUrl) {
        const errorMessage = 'Download URL is missing in the response';
        showErrorToast(errorMessage);
        onError(errorMessage);
        return;
      }
      
      // Create download link and trigger download in same tab
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', 'invoice.pdf'); // Force download attribute
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      const errorMessage = 'Error processing download: ' + (error instanceof Error ? error.message : String(error));
      showErrorToast(errorMessage);
      onError(errorMessage);
    }
  };
  
  // Function to trigger the invoice download
  const handleGenerateInvoice = () => {
    if (selectedRows.length === 0) return;
    
    setIsGenerating(true);
    
    // Convert order IDs to integers and ensure they are passed as a single flat array
    const orderIds = selectedRows.map(row => parseInt(row.orderId.toString(), 10));
    
    generateInvoiceQuery({
      variables: {
        order_ids: orderIds
      }
    });
  };

  return (
    <>
      {/* {hasDisabledStatusOrders && selectedRows.length > 0 && (
        <div className="text-danger small mb-1" style={{ maxWidth: '250px', lineHeight: '1.2' }}>
          Cannot generate invoices for orders with pending, processing, or being prepared status
        </div>
      )} */}
      <Button 
        variant="link" 
        className="text-body me-4 px-0"
        disabled={selectedRows.length === 0 || isGenerating || hasDisabledStatusOrders}
        onClick={handleGenerateInvoice}
        title={
           (selectedRows.length === 0 || isGenerating || hasDisabledStatusOrders)?
           "Cannot generate invoices for orders with pending, processing, or being prepared status"
            : "Generate and download invoices for selected orders"
        }
      >
        {isGenerating ? (
          <>
            <Spinner
              as="span"
              animation="border"
              size="sm"
              role="status"
              aria-hidden="true"
              className="fs-9 me-2"
              style={{ width: '0.9rem', height: '0.9rem' }}
            />
            <span>{t('orderButtons.generating')}</span>
          </>
        ) : (
          <>
            <FontAwesomeIcon icon={faFileInvoiceDollar} className="fs-9 me-2" />
            {t('orderButtons.downloadInvoices')}
          </>
        )}
      </Button>
    </>
  );
};

export default InvoiceButton;
